import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, booleanAttribute, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';

import { SelectColorDrawerComponent } from '@shared/_drawers/select-color';
import { SelectIconDrawerComponent } from '@shared/_drawers/select-icon';
import { AutoFocusDirective } from '@shared/_utils/autofocus.component';
import { TagComponent } from '@shared/_views/tag';

import { BaseDrawerComponent } from '@base/base-drawer.component';
import { Guid, TAG_COLORS, Tag, VzTag, VzTagOptions } from '@models';
import { HttpApiError } from '@models/api/http-api-error';
import { ApiService } from '@services/api.service';
import { CacheService } from '@services/cache.service';
import { StoreService } from '@services/store.service';
import { ErrorMessageComponent } from '@shared/_utils/error-message.component';
import { ErrorPanelComponent } from '@shared/_utils/error-panel';
import { AvatarComponent } from '@shared/_views/avatar';
import { ProjectBadgeComponent } from '@shared/_views/project-badge.component';

@Component({
    selector: 'vz-tag-drawer',
    templateUrl: './tag.component.html',
    styleUrls: ['./tag.component.less'],
    imports: [
        CommonModule, FormsModule,
        NzDrawerModule, NzInputModule, NzSelectModule, NzButtonModule, NzSegmentedModule, NzPopconfirmModule,
        NzCheckboxModule,
        AutoFocusDirective, TagComponent, SelectIconDrawerComponent, SelectColorDrawerComponent, ErrorMessageComponent,
        AvatarComponent, ProjectBadgeComponent, ErrorPanelComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@Tag('TagDrawerComponent')
export class TagDrawerComponent extends BaseDrawerComponent {

    tagId           = input<Guid>();
    parentId        = input<Guid>();
    projectId       = input<Guid>();
    canChangeType   = input(false, { transform: booleanAttribute });

    parents         = signal<VzTag[]>([]);
    showSelectIcon  = signal<boolean>(false);
    showSelectColor = signal<boolean>(false);
    showIcon        = signal<boolean>(false);
    showText        = signal<boolean>(false);
    showIconParent  = signal<boolean>(false);
    showTextParent  = signal<boolean>(false);
    showTagParent   = signal<boolean>(false);
    changed         = signal<boolean>(false);

    tagType         = signal<'org' | 'proj'>('proj');
    text            = signal<string>('Новый тег');
    description     = signal<string | null>(null);
    icon            = signal<string>('vzi-tag-lines');
    color           = signal<string>('#0060AA');
    uniqueGroup     = signal<boolean>(false);

    options = computed<number>(() =>
        (this.showIcon() ? 0 : VzTagOptions.HideIcon)
        | (this.showText() ? 0 : VzTagOptions.HideText)
        | (this.showIcon() && this.showIconParent() ? 0 : VzTagOptions.HideIconParent)
        | (this.showText() && this.showTextParent() ? 0 : VzTagOptions.HideTextParent)
        | (this.showTagParent() ? 0 : VzTagOptions.HideTagParent)
    );
    previewTags = computed<VzTag[]>(() => {
        const tag = new VzTag({
            id: 'new-main',
            parentId: this.parentId(),
            text: this.text(),
            description: this.description(),
            icon: this.icon(),
            color: this.color(),
            options: this.options(),
            parents: this.parents(),
            uniqueGroup: this.uniqueGroup()
        });
        return [
            tag,
            new VzTag({ parentId: 'new-main', parents: [...this.parents(), tag], color: '#c62828', options: 0, text: 'Текст и значок', icon: 'vzi-cool2'  }),
            new VzTag({ parentId: 'new-main', parents: [...this.parents(), tag], color: '#4a148c', options: 1, text: 'Только текст' }),
            new VzTag({ parentId: 'new-main', parents: [...this.parents(), tag], color: '#00695c', options: 2, icon: 'vzi-cool2'  }),
            new VzTag({ parentId: 'new-main', parents: [...this.parents(), tag], color: '#dd2c00', options: 3 }),
        ];
    });

    readonly _cache     = inject(CacheService);

    constructor(
        protected _store: StoreService,
        protected _cdr: ChangeDetectorRef,
        private _api: ApiService,
    ) {
        super(_store);
        effect(() => {
            const tid = this.tagId();
            // console.log('=[TagDrawerComponent]===> tid:', tid);
            this.changed.set(!tid);
            if (tid) {
                this._cache.get('tag', tid).subscribe({
                    next: st => {
                        const tag = st.item;
                        this.updateOptions(tag);
                        this.parents.set(tag?.parents || []);
                        if (tag) {
                            this.text.set(tag.text || '???');
                            this.icon.set(tag.icon || 'vzi-tag-lines');
                            this.color.set(tag.color || TAG_COLORS[Math.trunc(Math.random() * TAG_COLORS.length)]);
                            this.uniqueGroup.set(!!tag.uniqueGroup);
                            this.description.set(tag.description || '');
                            if (!tag.icon) {
                                this.showIcon.set(false);
                            }
                        }
                        else {
                            this._error.set(new HttpApiError('Не удалось загрузить тег.', 'Попробуйте позже.'));
                        }
                    },
                    error: err => {
                        this.updateOptions();
                        this._error.set(err);
                    }
                });
            }
            else {
                this.color.set(TAG_COLORS[Math.trunc(Math.random() * TAG_COLORS.length)]);
                this.updateOptions();
            }
        });
        effect(() => {
            const ptid = this.parentId();
            if (ptid) {
                this._cache.get('tag', ptid).subscribe(st => this.parents.set(st.item ? [st.item, ...(st.item.parents || [])] : []));
            }
        });
    }

    submit(): void {
        if (this.changed()) {
            const tag = new VzTag({
                orgId: this.activeOrgId,
                projectId: this.projectId(),
                parentId: this.parentId(),
                text: this.text(),
                description: this.description(),
                icon: this.icon(),
                color: this.color(),
                options: this.options(),
                uniqueGroup: this.uniqueGroup()
            });
            const tagId = this.tagId();
            if (tagId) {
                this._api.updateTag(tagId, tag).subscribe({
                    next: nt => {
                        if (nt) {
                            this._cache.put('tag', [nt]);
                            const st = this._store.getState('tags');
                            st.items[nt.id!] = nt;
                            this._store.setState('tags', st);
                        }
                        this.close(nt);
                    },
                    error: err => this.__error(err, true)
                });
            }
            else {
                if (this.tagType() == 'org') {
                    delete tag.projectId;
                }
                this._api.createTag(tag).subscribe({
                    next: tag => {
                        if (tag?.id) {
                            // this._dc.updateObject(tag);
                            this.close(tag);
                        }
                    },
                    error: err => this.__error(err, true)
                });
            }
        }
    }

    setIcon(icon?: string): void {
        if (icon) {
            this.icon.set(icon);
            this.changed.set(true);
        }
        this.showSelectIcon.set(false);
    }

    setColor(color?: string): void {
        if (color) {
            this.color.set(color);
            this.changed.set(true);
        }
        this.showSelectColor.set(false);
    }

    delete(): void {
        const tid = this.tagId();
        if (tid) {
            this._api.deleteTag(tid).subscribe({
                next: () => this.close(),
                error: err => this.__error(err)
            });
        }
    }

    updateOptions(tag?: VzTag): void {
        this.showIcon.set(!tag || !tag.options || !(tag.options & VzTagOptions.HideIcon));
        this.showText.set(!tag || !tag.options || !(tag.options & VzTagOptions.HideText));
        this.showIconParent.set(!tag || !tag.options || !(tag.options & VzTagOptions.HideIconParent));
        this.showTextParent.set(!tag || !tag.options || !(tag.options & VzTagOptions.HideTextParent));
        this.showTagParent.set(!tag || !tag.options || !(tag.options & VzTagOptions.HideTagParent));
    }

}
